/* eslint-disable react/react-in-jsx-scope */
import { ConfirmationModal, CustomDrawer, CustomSwitch } from '@components';
import { ClientOrganization, OrganizationFeature } from '@graphql';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { COLORS } from '@styles';
import { appContext } from '@utils';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';

type StatusFilter = 'all' | 'enabled' | 'disabled';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'enabled', label: 'Enabled' },
  { key: 'disabled', label: 'Disabled' },
];

const sx = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
    padding: '0 1rem',
    gap: '0.75rem',
  } as React.CSSProperties,
  search: {
    flexShrink: 0,
  },
  filterRow: {
    display: 'flex',
    gap: '0.5rem',
    flexShrink: 0,
  } as React.CSSProperties,
  filterButton: {
    borderRadius: '1rem',
    fontSize: '0.75rem',
    textTransform: 'none',
  } as React.CSSProperties,
  tableWrapper: {
    border: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    borderRadius: '0.5rem',
    overflowY: 'auto',
    flex: 1,
  } as React.CSSProperties,
  headCell: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: COLORS.NEUTRAL_LIGHT_0,
    borderBottom: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
  },
  bodyCell: {
    borderBottom: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
  },
  featureNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  } as React.CSSProperties,
  name: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: COLORS.NEUTRAL_WHITE,
  },
  description: {
    fontSize: '0.7rem',
    color: COLORS.NEUTRAL_LIGHT_0,
    marginTop: '0.1rem',
  },
  featureTypeChip: {
    backgroundColor: COLORS.NEUTRAL_DARK_1,
    color: COLORS.NEUTRAL_LIGHT_1,
    fontWeight: 600,
    fontSize: '0.65rem',
    height: '1.25rem',
  },
  addOnTypeChip: {
    backgroundColor: COLORS.HELP_NEUTRAL,
    color: COLORS.NEUTRAL_WHITE,
    fontWeight: 600,
    fontSize: '0.65rem',
    height: '1.25rem',
  },
  statusText: {
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  toggleCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '2rem',
  } as React.CSSProperties,
};

interface IOrganizationFeaturesDrawerProps {
  open: boolean;
  onClose: () => void;
  organization: ClientOrganization | null;
}

export const OrganizationFeaturesDrawer = observer(
  ({ open, onClose, organization }: IOrganizationFeaturesDrawerProps) => {
    const {
      organizationStore: { updateOrganizationFeature, refreshOrganizationViews },
    } = useContext(appContext);

    const features = useMemo(() => organization?.organizationFeatures ?? [], [organization]);
    const [enabled, setEnabled] = useState<Record<string, boolean>>({});
    const [pendingIds, setPendingIds] = useState<Record<string, boolean>>({});
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [pendingToggle, setPendingToggle] = useState<OrganizationFeature | null>(null);

    useEffect(() => {
      setEnabled(Object.fromEntries(features.map((f) => [f.id, Boolean(f.enabledAt)])));
    }, [features]);

    const requestToggle = (orgFeature: OrganizationFeature) => setPendingToggle(orgFeature);
    const closeToggleConfirmation = () => setPendingToggle(null);

    const confirmToggle = async () => {
      if (!pendingToggle) return;

      const orgFeature = pendingToggle;
      const nextEnabled = !enabled[orgFeature.id];
      const now = new Date().toISOString();

      setEnabled((prev) => ({ ...prev, [orgFeature.id]: nextEnabled }));
      setPendingIds((prev) => ({ ...prev, [orgFeature.id]: true }));
      closeToggleConfirmation();

      const result = await updateOrganizationFeature({
        id: orgFeature.id,
        organizationId: organization?.id,
        featureId: orgFeature.feature.id,
        enabledAt: nextEnabled ? now : null,
        disabledAt: nextEnabled ? null : now,
      });

      setPendingIds((prev) => ({ ...prev, [orgFeature.id]: false }));

      if (!result) {
        setEnabled((prev) => ({ ...prev, [orgFeature.id]: !nextEnabled }));
      }

      await refreshOrganizationViews(organization?.id);
    };

    const pendingToggleWillEnable = pendingToggle ? !enabled[pendingToggle.id] : false;

    const rows = useMemo(
      () =>
        features
          .filter((f) => f.feature.name.toLowerCase().includes(query.toLowerCase()))
          .filter((f) => {
            if (statusFilter === 'all') return true;
            return statusFilter === 'enabled' ? enabled[f.id] : !enabled[f.id];
          })
          .sort((a, b) => a.feature.name.localeCompare(b.feature.name)),
      [features, query, statusFilter, enabled],
    );

    return (
      <>
        <CustomDrawer
          open={open}
          onClose={onClose}
          headerLabel="Organization Features"
          headerIcon={<ToggleOnOutlinedIcon />}
        >
        <div style={sx.wrapper}>
          <TextField
            size="small"
            placeholder="Search features"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={sx.search}
            InputProps={{ endAdornment: <SearchIcon sx={{ color: COLORS.NEUTRAL_LIGHT_0 }} /> }}
          />
          <Box style={sx.filterRow}>
            {STATUS_FILTERS.map(({ key, label }) => (
              <Button
                key={key}
                size="small"
                variant={statusFilter === key ? 'contained' : 'outlined'}
                onClick={() => setStatusFilter(key)}
                style={sx.filterButton}
              >
                {label}
              </Button>
            ))}
          </Box>
          <Box style={sx.tableWrapper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={sx.headCell}>Feature</TableCell>
                  <TableCell style={sx.headCell}>Status</TableCell>
                  <TableCell style={sx.headCell} align="right">
                    Toggle
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((orgFeature) => {
                  const isAddOn = orgFeature.type === 'add-on';

                  return (
                    <TableRow key={orgFeature.id}>
                      <TableCell style={sx.bodyCell}>
                        <div style={sx.featureNameRow}>
                          <Typography style={sx.name}>{orgFeature.feature.name}</Typography>
                          <Chip
                            size="small"
                            label={isAddOn ? 'Add-on' : 'Feature'}
                            style={isAddOn ? sx.addOnTypeChip : sx.featureTypeChip}
                          />
                        </div>
                        <Typography style={sx.description}>{orgFeature.feature.description}</Typography>
                      </TableCell>
                      <TableCell style={sx.bodyCell}>
                        <Typography
                          style={{
                            ...sx.statusText,
                            color: enabled[orgFeature.id] ? COLORS.POSITIVE_NEUTRAL : COLORS.NEUTRAL_LIGHT_0,
                          }}
                        >
                          {enabled[orgFeature.id] ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </TableCell>
                      <TableCell style={sx.bodyCell} align="right">
                        <div style={sx.toggleCell}>
                          {pendingIds[orgFeature.id] ? (
                            <CircularProgress size={20} sx={{ color: COLORS.ELEKTRAFI_NEUTRAL }} />
                          ) : (
                            <Tooltip
                              title={isAddOn ? 'Add-ons are managed through the organization’s plan and billing.' : ''}
                            >
                              <span>
                                <CustomSwitch
                                  checked={!!enabled[orgFeature.id]}
                                  onChange={() => requestToggle(orgFeature)}
                                  disabled={isAddOn}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} style={{ ...sx.bodyCell, textAlign: 'center' }}>
                      <Typography style={sx.description}>No features match your search.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </div>
      </CustomDrawer>
      <ConfirmationModal
        icon={<ToggleOnOutlinedIcon />}
        title={`Are you sure you want to ${pendingToggleWillEnable ? 'enable' : 'disable'} ${pendingToggle?.feature.name} for ${organization?.name}?`}
        onClose={closeToggleConfirmation}
        isOpen={pendingToggle !== null}
        variant={pendingToggleWillEnable ? 'default' : 'warning'}
        onConfirm={confirmToggle}
        confirmationButtonLabel={pendingToggleWillEnable ? 'Enable Feature' : 'Disable Feature'}
        confirmationText={organization?.name || ''}
      >
        <p>
          This will {pendingToggleWillEnable ? 'enable' : 'disable'}{' '}
          <strong>{pendingToggle?.feature.name}</strong> for <strong>{organization?.name}</strong>.
        </p>
        <p>To proceed, please type the organization name: </p>
        <strong>{organization?.name}</strong>
      </ConfirmationModal>
      </>
    );
  },
);
